import React, { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Bold, Highlighter } from "lucide-react";

export default function ListBlockEditor({ block, onChange }) {
    const config = block.config ?? {
        ordered: false,
        items: [""]
    };

    const editorRefs = useRef([]);
    const [activeIndex, setActiveIndex] = useState(null);
    const [isBold, setIsBold] = useState(false);
    const [isHighlight, setIsHighlight] = useState(false);

    const update = (patch) => {
        onChange({
            config: {
                ...config,
                ...patch
            }
        });
    };

    const toViewableHtml = (text) => {
        if (!text) return "";
        return text
            .replace(/<b>/g, "<strong>")
            .replace(/<\/b>/g, "</strong>")
            .replace(
                /<h>([\s\S]*?)<\/h>/g,
                '<span class="text-highlight">$1</span>'
            );
    };

    const toStoredString = (html) => {
        return (html || "")
            .replace(/<strong>/g, "<b>")
            .replace(/<\/strong>/g, "</b>")
            .replace(
                /<span class="text-highlight">([\s\S]*?)<\/span>/g,
                "<h>$1</h>"
            );
    };

    const updateItem = (index) => {
        const items = [...config.items];
        items[index] = toStoredString(editorRefs.current[index].innerHTML);
        update({ items });
    };

    const addItem = () => {
        update({
            items: [...config.items, ""]
        });
    };

    const removeItem = (index) => {
        update({
            items: config.items.filter((_, i) => i !== index)
        });
    };

    const getHighlightSpan = () => {
        const sel = window.getSelection();
        if (!sel.rangeCount) return null;

        let node = sel.anchorNode;

        while (node) {
            if (
                node.nodeType === 1 &&
                node.tagName === "SPAN" &&
                node.classList.contains("text-highlight")
            ) {
                return node;
            }
            node = node.parentNode;
        }

        return null;
    };

    const syncStates = () => {
        setIsBold(document.queryCommandState("bold"));
        setIsHighlight(!!getHighlightSpan());
    };

    const wrapHighlight = () => {
        const sel = window.getSelection();
        if (!sel.rangeCount || sel.isCollapsed) return;

        const range = sel.getRangeAt(0);

        const span = document.createElement("span");
        span.className = "text-highlight";

        span.appendChild(range.extractContents());
        range.insertNode(span);

        sel.removeAllRanges();

        const after = document.createRange();
        after.setStartAfter(span);
        after.collapse(true);
        sel.addRange(after);
    };

    const unwrapHighlight = (span) => {
        while (span.firstChild)
            span.parentNode.insertBefore(span.firstChild, span);

        span.remove();
    };

    const applyFormat = (cmd) => {
        if (activeIndex == null) return;

        editorRefs.current[activeIndex]?.focus();

        if (cmd === "bold") {
            document.execCommand("bold");
        }

        if (cmd === "highlight") {
            const span = getHighlightSpan();

            if (span) unwrapHighlight(span);
            else wrapHighlight();
        }

        updateItem(activeIndex);
        syncStates();
    };

    useEffect(() => {
        config.items.forEach((item, i) => {
            const el = editorRefs.current[i];
            if (!el) return;

            const html = toViewableHtml(item);

            if (el.innerHTML !== html) {
                el.innerHTML = html;
            }
        });
    }, [block.id]);

    return (
        <div className="space-y-4">

            <label className="flex items-center gap-2">
                <input
                    type="checkbox"
                    checked={config.ordered}
                    onChange={(e) =>
                        update({ ordered: e.target.checked })
                    }
                />
                <span>لیست شماره‌دار</span>
            </label>

            <div className="flex gap-2">

                <button
                    onMouseDown={(e) => {
                        e.preventDefault();
                        applyFormat("bold");
                    }}
                    className={`p-2 border rounded ${
                        isBold
                            ? "bg-blue-600 text-white"
                            : "hover:bg-gray-100"
                    }`}
                >
                    <Bold size={16}/>
                </button>

                <button
                    onMouseDown={(e) => {
                        e.preventDefault();
                        applyFormat("highlight");
                    }}
                    className={`p-2 border rounded ${
                        isHighlight
                            ? "bg-yellow-400"
                            : "hover:bg-gray-100"
                    }`}
                >
                    <Highlighter size={16}/>
                </button>

            </div>

            <div className="space-y-3">

                {config.items.map((item, index) => (
                    <div
                        key={index}
                        className="flex gap-2 items-start"
                    >
                        <div className="mt-3 text-gray-500 font-semibold w-6 text-center">
                            {config.ordered ? `${index + 1}.` : "•"}
                        </div>

                        <div
                            ref={(el) => (editorRefs.current[index] = el)}
                            contentEditable
                            suppressContentEditableWarning
                            className="flex-1 min-h-[44px] p-3 border rounded-lg bg-gray-50 outline-none"
                            onFocus={() => {
                                setActiveIndex(index);
                                syncStates();
                            }}
                            onInput={() => updateItem(index)}
                            onMouseUp={syncStates}
                            onKeyUp={syncStates}
                        />

                        <button
                            onClick={() => removeItem(index)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                        >
                            <Trash2 size={16}/>
                        </button>
                    </div>
                ))}

            </div>

            <button
                onClick={addItem}
                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg"
            >
                <Plus size={16}/>
                افزودن آیتم
            </button>

        </div>
    );
}